import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import './MonitorBaas.css';
import './ConciliacaoConformidade.css';

/**
 * ConciliacaoConformidade.js — Fase 2
 * ---------------------------------------------------------------------------
 * Conciliação bancária: Prestação de Contas ⇄ Compra do Cartão (Stark).
 * Reúne o Analytics de Conformidade (KPIs) + a lista de conciliação por
 * prestação + as compras órfãs.
 *
 * Fonte de dados (routesBaasBankingAccount, Fase 2):
 *   POST /api/baas/account/conciliacao/run                → sincroniza + concilia
 *   GET  /api/baas/account/conciliacao/resumo             → KPIs
 *   GET  /api/baas/account/conciliacao?conformidade=&limit=&offset=  → lista paginada
 *   GET  /api/baas/account/conciliacao/orfas?limit=&offset=          → órfãs paginadas
 *   POST /api/baas/account/conciliacao/:despesaId/revisar → tratar divergência
 *
 * COMPORTAMENTO "ESTILO BANCO":
 *   - Polling leve (só rotas de LEITURA, nunca /run) que PAUSA quando a aba
 *     não está visível. As transações "entram sozinhas" na tela sem custo na
 *     Stark — quem fala com a Stark é o cron em background (Entregável 1).
 *   - Paginação server-side (limit/offset). Página pequena, estilo extrato.
 *   - Linhas novas entram com fade-in; uma pílula avisa "N nova(s)".
 *   - Tempo relativo de última atualização ("há 20s").
 *
 * `onAbrirConfig`: callback opcional (ex: navegação de rota) chamado ao
 * clicar na engrenagem do header. Se não vier, o botão não aparece — assim
 * a tela não fica com um botão morto enquanto a integração de rota não
 * estiver plugada no componente pai.
 *
 * Reusa o design system do MonitorBaas (mb-*) e acrescenta os elementos
 * próprios da conformidade (cc-*).
 */

const PAGE_SIZE = 25;
const POLL_MS = 20000; // 20s — cadência de banco, sem marteлar a API.

const fmt = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);

const fmtDate = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

// Tempo relativo curto para o rótulo "atualizado há …".
const fmtRelative = (ts, now) => {
  if (!ts) return '—';
  const s = Math.max(0, Math.round((now - ts) / 1000));
  if (s < 5) return 'agora mesmo';
  if (s < 60) return `há ${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `há ${m} min`;
  const h = Math.round(m / 60);
  return `há ${h} h`;
};

const envLabelOf = (env) =>
  ({ production: 'PRODUÇÃO', sandbox: 'SANDBOX' }[env] || String(env || '').toUpperCase() || '—');

// Metadados visuais de cada estado de conformidade.
const CONF = {
  conciliado: { label: 'Conciliado', cls: 'is-ok', icon: '✓' },
  sugerido: { label: 'Sugerido', cls: 'is-suggest', icon: '?' },
  divergente: { label: 'Divergente', cls: 'is-warn', icon: '≠' },
  sem_lastro: { label: 'Sem lastro', cls: 'is-neutral', icon: '○' },
  orfa: { label: 'Órfã', cls: 'is-danger', icon: '!' },
};

const TABS = [
  { key: 'todos', label: 'Todas' },
  { key: 'sugerido', label: 'Sugeridas' },
  { key: 'divergente', label: 'Divergentes' },
  { key: 'sem_lastro', label: 'Sem lastro' },
  { key: 'conciliado', label: 'Conciliadas' },
  { key: 'orfas', label: 'Compras órfãs' },
];

// Chave estável de cada item, usada para detectar "linha nova" entre pollings.
const rowKeyOf = (tab, it) =>
  tab === 'orfas' ? `o:${it.id}` : `c:${it.id}`;

export default function ConciliacaoConformidade({ onAbrirConfig } = {}) {
  const [connected, setConnected] = useState(null); // null = carregando
  const [environment, setEnvironment] = useState(null);

  const [resumo, setResumo] = useState(null);
  const [items, setItems] = useState([]);
  const [orfas, setOrfas] = useState([]);
  const [tab, setTab] = useState('divergente');

  // Paginação da lista visível (compartilhada entre abas; reseta ao trocar).
  const [offset, setOffset] = useState(0);
  const [pageMeta, setPageMeta] = useState({ total: 0, has_more: false, limit: PAGE_SIZE });

  const [loading, setLoading] = useState(true);   // carregamento inicial (tela toda)
  const [listLoading, setListLoading] = useState(false); // só a lista (troca de aba/página)
  const [rodando, setRodando] = useState(false);

  // "Ao vivo": última atualização e contador de novidades do polling.
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  const [agora, setAgora] = useState(Date.now());
  const [novas, setNovas] = useState(0);
  const [flashIds, setFlashIds] = useState(() => new Set());
  const [pausado, setPausado] = useState(false);

  const aliveRef = useRef(true);
  const seenRef = useRef(new Set());   // chaves já vistas (para marcar as novas)
  const tabRef = useRef(tab);
  const offsetRef = useRef(offset);
  const pollRef = useRef(null);

  useEffect(() => { aliveRef.current = true; return () => { aliveRef.current = false; }; }, []);
  useEffect(() => { tabRef.current = tab; }, [tab]);
  useEffect(() => { offsetRef.current = offset; }, [offset]);

  const isProd = environment === 'production';

  // ── Fetchers de baixo nível ────────────────────────────────────────────────
  const fetchResumo = useCallback(async () => {
    try {
      const { data } = await api.get('/api/baas/account/conciliacao/resumo');
      if (!aliveRef.current) return;
      setResumo(data);
      setEnvironment(data.environment || null);
      setConnected(true);
    } catch (e) {
      if (!aliveRef.current) return;
      if (e?.response?.status === 409) { setConnected(false); return; }
      toast.error(e?.response?.data?.error || 'Erro ao carregar o resumo de conformidade.');
    }
  }, []);

  // Busca uma página da lista (ou das órfãs). `animate` marca linhas novas.
  const fetchPagina = useCallback(async (alvoTab, alvoOffset, { animate } = {}) => {
    const isOrfas = alvoTab === 'orfas';
    const url = isOrfas ? '/api/baas/account/conciliacao/orfas' : '/api/baas/account/conciliacao';
    const params = { limit: PAGE_SIZE, offset: alvoOffset };
    if (!isOrfas && alvoTab && alvoTab !== 'todos') params.conformidade = alvoTab;

    const { data } = await api.get(url, { params });
    if (!aliveRef.current) return;

    const lista = Array.isArray(data?.items) ? data.items : [];
    const meta = data?.page || { total: lista.length, has_more: false, limit: PAGE_SIZE };

    // Detecta linhas ainda não vistas para o fade-in + contador de novidades.
    let novasChaves = [];
    if (animate) {
      for (const it of lista) {
        const k = rowKeyOf(alvoTab, it);
        if (!seenRef.current.has(k)) novasChaves.push(k);
      }
    }
    for (const it of lista) seenRef.current.add(rowKeyOf(alvoTab, it));

    if (isOrfas) setOrfas(lista); else setItems(lista);
    setPageMeta({ total: Number(meta.total || 0), has_more: !!meta.has_more, limit: Number(meta.limit || PAGE_SIZE) });
    setUltimaAtualizacao(Date.now());

    if (novasChaves.length > 0) {
      setNovas((n) => n + novasChaves.length);
      setFlashIds(new Set(novasChaves));
      // Limpa o destaque depois da animação (não acumula entre pollings).
      setTimeout(() => { if (aliveRef.current) setFlashIds(new Set()); }, 1600);
    }
  }, []);

  // Recarga silenciosa do polling: resumo + página atual, sem spinner.
  const reloadQuiet = useCallback(async () => {
    try {
      await Promise.all([
        fetchResumo(),
        fetchPagina(tabRef.current, offsetRef.current, { animate: true }),
      ]);
    } catch (e) {
      // Polling é best-effort: um erro pontual não vira toast (evita spam).
      // O próximo tick tenta de novo.
    }
  }, [fetchResumo, fetchPagina]);

  // ── Carregamento inicial ────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchResumo();
      try { await fetchPagina(tabRef.current, 0, { animate: false }); } catch { /* trata no fetch */ }
      if (aliveRef.current) setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Troca de aba/página recarrega só a lista (com skeleton curto) ──────────
  const carregarLista = useCallback(async (alvoTab, alvoOffset) => {
    setListLoading(true);
    try {
      await fetchPagina(alvoTab, alvoOffset, { animate: false });
    } catch (e) {
      if (aliveRef.current) toast.error(e?.response?.data?.error || 'Erro ao carregar a lista.');
    } finally {
      if (aliveRef.current) setListLoading(false);
    }
  }, [fetchPagina]);

  useEffect(() => {
    if (connected !== true) return;
    carregarLista(tab, offset);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, offset, connected]);

  // ── Polling "ao vivo" que PAUSA quando a aba não está visível ──────────────
  useEffect(() => {
    if (connected !== true) return undefined;

    const parar = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
    const comecar = () => {
      parar();
      pollRef.current = setInterval(() => {
        setAgora(Date.now());
        if (document.visibilityState === 'visible') reloadQuiet();
      }, POLL_MS);
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        setPausado(false);
        reloadQuiet();      // atualiza na hora ao voltar pra aba
        comecar();
      } else {
        setPausado(true);
        parar();            // não gasta rede em aba de fundo
      }
    };

    if (document.visibilityState === 'visible') comecar();
    else setPausado(true);

    document.addEventListener('visibilitychange', onVisibility);
    return () => { parar(); document.removeEventListener('visibilitychange', onVisibility); };
  }, [connected, reloadQuiet]);

  // Mantém o rótulo "há Xs" vivo mesmo sem polling disparar.
  useEffect(() => {
    const t = setInterval(() => setAgora(Date.now()), 10000);
    return () => clearInterval(t);
  }, []);

  // ── Ações ───────────────────────────────────────────────────────────────────
  const trocarAba = (nova) => {
    if (nova === tab) return;
    seenRef.current = new Set(); // cada aba tem seu próprio universo de "vistos"
    setNovas(0);
    setFlashIds(new Set());
    setOffset(0);
    setTab(nova);
  };

  const irParaTopo = () => {
    setNovas(0);
    if (offset !== 0) setOffset(0);
    else carregarLista(tab, 0);
  };

  const handleConciliar = async () => {
    if (rodando) return;
    setRodando(true);
    try {
      const { data } = await api.post('/api/baas/account/conciliacao/run', {});
      toast.success(
        `Conciliação concluída: ${data.conciliados} ok, ${data.sugeridos ?? 0} sugerida(s), `
        + `${data.divergentes} divergente(s), ${data.orfas} órfã(s).`
      );
      seenRef.current = new Set();
      setNovas(0);
      setOffset(0);
      await fetchResumo();
      await carregarLista(tab, 0);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Falha ao executar a conciliação.');
    } finally {
      if (aliveRef.current) setRodando(false);
    }
  };

  const handleRevisar = async (despesaId) => {
    const observacao = window.prompt('Observação da revisão (opcional):', '') ?? '';
    try {
      await api.post(`/api/baas/account/conciliacao/${despesaId}/revisar`, { observacao });
      toast.success('Divergência marcada como revisada.');
      await Promise.all([fetchResumo(), carregarLista(tab, offset)]);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Erro ao revisar.');
    }
  };

  // Confirma um match SUGERIDO (amarelo) → promove a conciliado e o trava.
  const handleConfirmar = async (despesaId) => {
    try {
      await api.post(`/api/baas/account/conciliacao/${despesaId}/confirmar`, {});
      toast.success('Sugestão confirmada e conciliada.');
      await Promise.all([fetchResumo(), carregarLista(tab, offset)]);
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Erro ao confirmar a sugestão.');
    }
  };

  const taxa = resumo?.prestacoes?.taxa_conciliacao ?? 0;

  const kpis = useMemo(() => {
    const p = resumo?.prestacoes || {};
    const c = resumo?.compras || {};
    return {
      conciliados: p.conciliados || 0,
      sugeridos: p.sugeridos || 0,
      sugeridosAbertos: p.sugeridos_abertos || 0,
      divergentes: p.divergentes || 0,
      divergentesAbertas: p.divergentes_abertas || 0,
      semLastro: p.sem_lastro || 0,
      totalPrest: p.total || 0,
      orfas: c.orfas || 0,
    };
  }, [resumo]);

  // Faixa da página atual: "1–25 de 132".
  const faixaPagina = useMemo(() => {
    const total = pageMeta.total || 0;
    if (total === 0) return '0 de 0';
    const ini = offset + 1;
    const fim = offset + (tab === 'orfas' ? orfas.length : items.length);
    return `${ini}–${fim} de ${total}`;
  }, [pageMeta.total, offset, tab, orfas.length, items.length]);

  // ── Estados de conexão ──────────────────────────────────────────────────
  if (connected === null) {
    return (
      <div className="mb-page">
        <div className="cc-shell">
          <div className="mb-empty"><p>Carregando conformidade...</p></div>
        </div>
      </div>
    );
  }
  if (connected === false) {
    return (
      <div className="mb-page">
        <div className="cc-shell">
          <div className="mb-empty">
            <span className="mb-empty-icon">🔌</span>
            <p>Nenhuma conta Stark Bank conectada.</p>
            <small>Conecte a conta para conciliar prestações com as compras do cartão.</small>
          </div>
        </div>
      </div>
    );
  }

  const listaVisivel = tab === 'orfas' ? orfas : items;

  return (
    <div className="mb-page">
      <div className="cc-shell">
        {/* ── HEADER ───────────────────────────────────────────────────────── */}
        <div className="mb-header">
          <div>
            <h2>
              Conciliação &amp; Conformidade
              {environment && (
                <span className={`mb-env-badge ${isProd ? 'is-prod' : 'is-sbx'}`}>
                  ● {envLabelOf(environment)}
                </span>
              )}
            </h2>
            <p>Cada prestação de contas confrontada com a compra real no cartão corporativo.</p>
          </div>

          <div className="cc-header-actions">
            <span className={`cc-live ${pausado ? 'is-paused' : ''}`} title={pausado ? 'Pausado (aba em segundo plano)' : 'Atualizando automaticamente'}>
              <span className="cc-live-dot" />
              {pausado ? 'Pausado' : 'Ao vivo'}
              <span className="cc-live-time">· atualizado {fmtRelative(ultimaAtualizacao, agora)}</span>
            </span>
            {onAbrirConfig && (
              <button
                className="cc-icon-btn"
                onClick={onAbrirConfig}
                title="Configurar escada de cobrança"
                aria-label="Configurar escada de cobrança"
              >
                ⚙
              </button>
            )}
            <button className="mb-btn-primary" onClick={handleConciliar} disabled={rodando}>
              {rodando ? 'Conciliando…' : '⟳ Conciliar agora'}
            </button>
          </div>
        </div>

        {!isProd && (
          <div className="mb-env-alert">
            <strong>Ambiente de TESTES (Sandbox).</strong>
            <span> A conformidade aqui não representa gastos reais.</span>
          </div>
        )}

        {/* ── PAINEL DE RESUMO — medidor + KPIs, uma faixa só ─────────────── */}
        <div className="cc-summary">
          <div className="cc-summary-gauge">
            <div className="cc-gauge" style={{ '--pct': `${Math.max(0, Math.min(100, taxa))}` }}>
              <div className="cc-gauge-hole">
                <strong>{taxa}%</strong>
                <span>conciliado</span>
              </div>
            </div>
            <div className="cc-gauge-legend">
              <p>Taxa de conciliação</p>
              <small>{kpis.conciliados} de {kpis.totalPrest} prestações batem com uma compra.</small>
            </div>
          </div>

          <div className="cc-summary-kpis">
            <div className="cc-kpi is-ok">
              <span className="cc-kpi-icon">✓</span>
              <div><strong>{kpis.conciliados}</strong><span>Conciliadas</span></div>
            </div>
            <div className="cc-kpi is-suggest">
              <span className="cc-kpi-icon">?</span>
              <div>
                <strong>{kpis.sugeridos}</strong>
                <span>Sugeridas{kpis.sugeridosAbertos > 0 ? ` · ${kpis.sugeridosAbertos} a confirmar` : ''}</span>
              </div>
            </div>
            <div className="cc-kpi is-warn">
              <span className="cc-kpi-icon">≠</span>
              <div>
                <strong>{kpis.divergentes}</strong>
                <span>Divergentes{kpis.divergentesAbertas > 0 ? ` · ${kpis.divergentesAbertas} abertas` : ''}</span>
              </div>
            </div>
            <div className="cc-kpi is-neutral">
              <span className="cc-kpi-icon">○</span>
              <div><strong>{kpis.semLastro}</strong><span>Sem lastro</span></div>
            </div>
            <div className="cc-kpi is-danger">
              <span className="cc-kpi-icon">!</span>
              <div><strong>{kpis.orfas}</strong><span>Compras órfãs</span></div>
            </div>
          </div>
        </div>

        {/* ── ABAS ─────────────────────────────────────────────────────────── */}
        <div className="cc-tabs">
          {TABS.map((t) => {
            const count =
              t.key === 'sugerido' ? kpis.sugeridos :
              t.key === 'divergente' ? kpis.divergentes :
              t.key === 'sem_lastro' ? kpis.semLastro :
              t.key === 'conciliado' ? kpis.conciliados :
              t.key === 'orfas' ? kpis.orfas : kpis.totalPrest;
            return (
              <button
                key={t.key}
                className={`cc-tab ${tab === t.key ? 'active' : ''}`}
                onClick={() => trocarAba(t.key)}
              >
                {t.label}
                <span className="cc-tab-count">{count}</span>
              </button>
            );
          })}
        </div>

        {/* ── Pílula "N novas transações" (some sozinha) ───────────────────── */}
        {novas > 0 && (
          <button className="cc-novas-pill" onClick={irParaTopo}>
            ⟳ {novas} nova{novas > 1 ? 's' : ''} transação{novas > 1 ? 'ões' : ''} · ver recentes
          </button>
        )}

        {/* ── CONTEÚDO ─────────────────────────────────────────────────────── */}
        <section className="mb-panel">
          {loading || listLoading ? (
            // Skeleton — sensação "lazy" de banco, sem flash de tela vazia.
            <div className="cc-list" aria-busy="true">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="cc-row cc-row--skel">
                  <div className="cc-skel cc-skel-badge" />
                  <div className="cc-row-main">
                    <div className="cc-skel cc-skel-line" style={{ width: '55%' }} />
                    <div className="cc-skel cc-skel-line cc-skel-line--sm" style={{ width: '38%' }} />
                  </div>
                  <div className="cc-skel cc-skel-value" />
                </div>
              ))}
            </div>
          ) : tab === 'orfas' ? (
            // ── Compras órfãs ──
            orfas.length === 0 ? (
              <div className="mb-empty">
                <span className="mb-empty-icon">🎯</span>
                <p>Nenhuma compra órfã.</p>
                <small>Toda compra no cartão tem uma prestação correspondente.</small>
              </div>
            ) : (
              <div className="cc-list">
                {orfas.map((o) => {
                  const k = rowKeyOf('orfas', o);
                  return (
                    <div key={o.id} className={`cc-row cc-row--orfa ${flashIds.has(k) ? 'is-new' : ''}`}>
                      <div className="cc-row-badge is-danger" title="Compra sem prestação">!</div>
                      <div className="cc-row-main">
                        <div className="cc-row-title">
                          {o.merchant_display_name || o.merchant_name || 'Compra no cartão'}
                          {o.card_ending && <span className="cc-card-tag">•••• {o.card_ending}</span>}
                        </div>
                        <div className="cc-row-sub">
                          {o.holder_name || 'Titular desconhecido'}
                          {o.motorista_vinculado ? ` · ${o.motorista_vinculado}` : ' · sem motorista vinculado'}
                          {' · '}{fmtDate(o.stark_created_at)}
                        </div>
                      </div>
                      <div className="cc-row-value is-danger">{fmt(o.valor)}</div>
                    </div>
                  );
                })}
              </div>
            )
          ) : listaVisivel.length === 0 ? (
            <div className="mb-empty">
              <span className="mb-empty-icon">📄</span>
              <p>Nenhuma prestação neste filtro.</p>
              <small>Rode "Conciliar agora" para avaliar as prestações do período.</small>
            </div>
          ) : (
            <div className="cc-list">
              {listaVisivel.map((it) => {
                const meta = CONF[it.conformidade] || CONF.sem_lastro;
                const temDif = it.conformidade === 'divergente';
                const ehSugerido = it.conformidade === 'sugerido';
                const k = rowKeyOf(tab, it);
                return (
                  <div key={it.id} className={`cc-row cc-row--${meta.cls} ${flashIds.has(k) ? 'is-new' : ''}`}>
                    <div className={`cc-row-badge ${meta.cls}`} title={meta.label}>{meta.icon}</div>

                    <div className="cc-row-main">
                      <div className="cc-row-title">
                        {it.despesa_descricao || `Prestação #${it.despesa_id}`}
                        {it.card_ending && <span className="cc-card-tag">•••• {it.card_ending}</span>}
                      </div>
                      <div className="cc-row-sub">
                        {it.motorista_nome || 'Motorista —'}
                        {' · '}{fmtDate(it.data_vencimento)}
                        {it.merchant_name ? ` · ${it.merchant_name}` : ''}
                        {it.criterio ? ` · match: ${it.criterio}` : ''}
                      </div>
                    </div>

                    <div className="cc-row-figures">
                      {temDif ? (
                        <div className="cc-dif">
                          <span className="cc-dif-declarado">{fmt(it.valor_prestacao)}</span>
                          <span className="cc-dif-arrow">vs</span>
                          <span className="cc-dif-real">{fmt(it.valor_compra)}</span>
                          <span className="cc-dif-delta">
                            {Number(it.diferenca) > 0 ? '+' : ''}{fmt(it.diferenca)}
                          </span>
                        </div>
                      ) : (
                        <div className="cc-row-value">{fmt(it.valor_prestacao)}</div>
                      )}
                      <span className={`cc-status-chip ${meta.cls}`}>{meta.label}</span>
                    </div>

                    {temDif && (
                      <div className="cc-row-action">
                        {it.revisado ? (
                          <span className="cc-revisado" title={it.observacao || ''}>revisada ✓</span>
                        ) : (
                          <button className="mb-btn-ghost cc-btn-sm" onClick={() => handleRevisar(it.despesa_id)}>
                            Revisar
                          </button>
                        )}
                      </div>
                    )}

                    {ehSugerido && (
                      <div className="cc-row-action">
                        {it.match_confirmado ? (
                          <span className="cc-revisado">confirmada ✓</span>
                        ) : (
                          <button className="mb-btn-primary cc-btn-sm" onClick={() => handleConfirmar(it.despesa_id)}>
                            Confirmar
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Rodapé de paginação (estilo extrato) ───────────────────────── */}
          {!loading && !listLoading && listaVisivel.length > 0 && (
            <div className="cc-pager">
              <span className="cc-pager-info">{faixaPagina}</span>
              <div className="cc-pager-controls">
                <button
                  className="mb-btn-ghost cc-btn-sm"
                  onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                  disabled={offset === 0}
                >
                  ← Anterior
                </button>
                <button
                  className="mb-btn-ghost cc-btn-sm"
                  onClick={() => setOffset((o) => o + PAGE_SIZE)}
                  disabled={!pageMeta.has_more}
                >
                  Próxima →
                </button>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}